"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameplayRenderer = exports.LetterboxRenderingType = void 0;
const tslib_1 = require("tslib");
const phaser_1 = tslib_1.__importDefault(require("phaser"));
const grayscalepipeline_plugin_js_1 = tslib_1.__importDefault(require("phaser3-rex-plugins/plugins/grayscalepipeline-plugin.js"));
const utils_1 = require("./utils");
const GameplayScene_1 = require("./gameplayRenderer/GameplayScene");
const GameplayAvatar_1 = require("./gameplayRenderer/GameplayAvatar");
const GameplayUtils_1 = require("./gameplayRenderer/GameplayUtils");
const consts_1 = require("./consts");
const MetaElement_1 = require("./webComponents/MetaElement");
const DialogueElement_1 = require("./webComponents/DialogueElement");
const ChoiceElement_1 = require("./webComponents/ChoiceElement");
const CommentCountElement_1 = require("./webComponents/CommentCountElement");
const GameUIStyle_1 = require("./models/GameUIStyle");
const Analytics_1 = require("./models/Analytics");
const DropoffAnalyticsElement_1 = require("./webComponents/DropoffAnalyticsElement");
var LetterboxRenderingType;
(function (LetterboxRenderingType) {
    LetterboxRenderingType["Standard"] = "standard";
    LetterboxRenderingType["Letterbox"] = "letterbox";
})(LetterboxRenderingType = exports.LetterboxRenderingType || (exports.LetterboxRenderingType = {}));
class GameplayRenderer {
    constructor(options) {
        var _a;
        let { parentElement, canvasStyle, domLayerStyle, width, height, avatarResolver, backgroundColor, targetFpsRate, getDevicePixelRatio: _getDevicePixelRatio, debug, isCheckingHtmlOnTopBeforeclick, maxHeightOfCanvas, alwaysRealDevicePixelRatio, optimization, mouseEventsEnabled, styles, isMultiplayer, viewPortSize, letterboxRenderingType, letterboxViewportSettings, } = options;
        this._parentElement = parentElement instanceof HTMLElement
            ? parentElement
            : document.body.querySelector(parentElement);
        this._stylesAlias = (_a = styles === null || styles === void 0 ? void 0 : styles.alias) !== null && _a !== void 0 ? _a : GameUIStyle_1.GameUIStyle.Default;
        this._isMultiplayer = isMultiplayer !== null && isMultiplayer !== void 0 ? isMultiplayer : false;
        this._viewPortSize = viewPortSize;
        // make sure that we always have real value if not specifed from outside
        if (alwaysRealDevicePixelRatio === undefined) {
            alwaysRealDevicePixelRatio = window ? (window.devicePixelRatio || 1) : 1;
        }
        if (letterboxRenderingType && letterboxViewportSettings) {
            GameplayRenderer.letterboxRenderingType = letterboxRenderingType;
        }
        else {
            GameplayRenderer.letterboxRenderingType = LetterboxRenderingType.Standard;
        }
        if (letterboxViewportSettings) {
            GameplayRenderer.letterboxViewportSettings = Object.assign({}, letterboxViewportSettings);
        }
        else {
            GameplayRenderer.letterboxViewportSettings = GameplayRenderer.letterboxDefaultViewportSettings;
        }
        const actualGetDevicePixelRatio = _getDevicePixelRatio || GameplayUtils_1.getDevicePixelRatio;
        GameplayRenderer.alwaysRealDevicePixelRatio = alwaysRealDevicePixelRatio;
        GameplayRenderer.mouseEventsEnabled = mouseEventsEnabled;
        let game_w = width;
        let game_h = height;
        let game_ratio = game_w / game_h;
        if (maxHeightOfCanvas === undefined) {
            // calculate canvas size depending on real devicePixelRatio
            game_w = width * actualGetDevicePixelRatio();
            game_h = height * actualGetDevicePixelRatio();
        }
        else {
            // calculate canvas size with restriction of max height
            game_h = Math.min(maxHeightOfCanvas, game_h * alwaysRealDevicePixelRatio);
            game_w = game_h * game_ratio;
        }
        game_w = Math.floor(game_w);
        game_h = Math.floor(game_h);
        // calculate how much we have rescaled the canvas
        // const tempFullSize = width * alwaysRealDevicePixelRatio;
        GameplayRenderer.canvasScale = game_w / width;
        (0, GameplayUtils_1.setDevicePixelRatio)(GameplayRenderer.canvasScale);
        const fps = targetFpsRate !== undefined ? {
            target: targetFpsRate,
        } : {};
        const config = {
            backgroundColor: backgroundColor || '#000000',
            parent: parentElement,
            canvasStyle,
            scale: {
                mode: phaser_1.default.Scale.NONE,
                autoCenter: phaser_1.default.Scale.Center.NO_CENTER,
                width: game_w,
                height: game_h,
                expandParent: false,
                zoom: 1
            },
            disableContextMenu: true,
            banner: false,
            autoFocus: false,
            input: {
                activePointers: 1,
                windowEvents: true,
                touch: true,
                mouse: true,
                keyboard: false,
                gamepad: false,
            },
            transparent: false,
            scene: [],
            plugins: {
                global: [{
                        key: 'rexGrayScalePipeline',
                        plugin: grayscalepipeline_plugin_js_1.default,
                        start: true
                    }]
            },
            fps,
            render: {
                antialiasGL: false,
                batchSize: 512,
            },
        };
        GameplayRenderer.isCheckingHtmlOnTopBeforeclick = isCheckingHtmlOnTopBeforeclick;
        GameplayRenderer.imageMapId = 1;
        GameplayRenderer.imageMap = {};
        GameplayRenderer.aggressiveMemoryManagement = (optimization === null || optimization === void 0 ? void 0 : optimization.aggressiveMemoryManagement) || false;
        GameplayRenderer.onAssetUnloadHandler = (optimization === null || optimization === void 0 ? void 0 : optimization.onAssetUnloadHandler) || ((_url) => false);
        this.game = new phaser_1.default.Game(config);
        this.game.scene.add('GameplayScene', GameplayScene_1.GameplayScene, true, {
            avatarResolver,
            getDevicePixelRatio: actualGetDevicePixelRatio,
            debug: debug,
            renderer: this,
        });
        // this._startInitializingTime = Date.now();
        this.avatarResolver = avatarResolver;
        this.createDOMLayer(domLayerStyle);
        this.registerCustomWebComponents();
        // MICHAL: caution - canvas need to be properly sized in CSS on the outside
        // this.game.canvas.style.width = `${width}px`;
        // this.game.canvas.style.height = `${height}px`;
    }
    static SVG_DEFAULT_SCALE() {
        // return Math.min(3, Math.max(getDevicePixelRatio(), 2));
        return 2;
    }
    get stylesAlias() {
        return this._stylesAlias;
    }
    updateStyleAlias(stylesAlias) {
        var _a, _b;
        this._stylesAlias = stylesAlias;
        (_b = (_a = this.mainScene) === null || _a === void 0 ? void 0 : _a.textBubble) === null || _b === void 0 ? void 0 : _b.updateStylesAlias(stylesAlias);
    }
    get isMultiplayer() {
        return this._isMultiplayer;
    }
    /**
     * adding source-atop since it's not included out of the box
     * see other blend modes here:
     * https://github.com/pixijs/pixijs/blob/dev/packages/core/src/state/utils/mapWebGLBlendModesToPixi.ts
     */
    createBlendModes() {
        const webGLRenderer = this.game.renderer;
        const gl = webGLRenderer.gl;
        const mode = [gl.DST_ALPHA, gl.ONE_MINUS_SRC_ALPHA]; // [src, dest]
        const equation = gl.FUNC_ADD;
        // @ts-ignore: Bad phaser type definitions. mode is not a Function, it's a GLEnum[]
        webGLRenderer.updateBlendMode(phaser_1.default.BlendModes.SOURCE_ATOP, mode, equation);
    }
    createDOMLayer(customStyle) {
        var _a, _b, _c, _d;
        this._domLayer = document.createElement('div');
        const defaultStyles = `
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
        `.replace(/\s+/g, '');
        const styles = `${defaultStyles}${customStyle || ''}`;
        this._domLayer.setAttribute('style', styles);
        const width = (_b = (_a = this._viewPortSize) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : innerWidth;
        const height = (_d = (_c = this._viewPortSize) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : innerHeight;
        this._domLayer.style.setProperty('--dor-vw', `${width / 100}px`);
        this._domLayer.style.setProperty('--dor-vh', `${height / 100}px`);
        this._parentElement.appendChild(this._domLayer);
    }
    registerCustomWebComponents() {
        // prevent defining more than once
        if (customElements.get('dor-meta')) {
            return;
        }
        customElements.define('dor-meta', MetaElement_1.MetaElement);
        customElements.define('dor-dialogue', DialogueElement_1.DialogueElement);
        customElements.define('dor-choice', ChoiceElement_1.ChoiceElement);
        customElements.define('dor-comments-count', CommentCountElement_1.CommentsCountElement);
        customElements.define('dor-dropoff-analytics', DropoffAnalyticsElement_1.DropoffAnalyticsElement);
    }
    get mainScene() {
        var _a, _b, _c, _d;
        return (_d = (_c = (_b = (_a = this.game) === null || _a === void 0 ? void 0 : _a.scene) === null || _b === void 0 ? void 0 : _b.keys) === null || _c === void 0 ? void 0 : _c.GameplayScene) !== null && _d !== void 0 ? _d : undefined;
    }
    get domLayer() {
        return this._domLayer;
    }
    destroy() {
        // this.game.textures.destroy();
        // this.game.scene.remove('GameplayScene');
        // this.game.scene.destroy();
        this.game.destroy(true, false);
        // FIXME: if you fully destroy game it doens't start again
        // this.game.destroy(true, true);
        this._parentElement.removeChild(this._domLayer);
    }
    waitUntilReady() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // console.error('waiting for phaser 3 to initialize');
            const checker = () => {
                const checkResult = this.game.scene.isBooted && !this.game.scene.isProcessing && !!this.mainScene;
                return checkResult || undefined;
            };
            const result = yield (0, utils_1.periodicChecker)(checker, 50);
            // console.error(`phaser 3 initialized! it took ${(Date.now() - this._startInitializingTime) / 1000}s`);
            this.createBlendModes();
            return result;
        });
    }
    static removeTexture(scene, textureKey, url) {
        this.imageMap[url] = '';
        scene.textures.remove(textureKey);
    }
    static getImageId(url, scene, imageType = 'png', preventCache = false) {
        if (!this.imageMap[url]) {
            this.imageMap[url] = `image-${this.imageMapId++}`;
        }
        const textureId = this.imageMap[url];
        if (!scene.textures.exists(textureId) &&
            !scene.load.keyExists({ key: textureId, type: 'image', hasCacheConflict: () => false })
            || preventCache) {
            if (/^http/.test(url)) {
                scene.load.setCORS('anonymous');
            }
            else {
                scene.load.setCORS();
            }
            // start loading if the texture is not yet loaded & parsed and not being loading at the moment
            if (url.startsWith('data:image/')) {
                const image = new Image();
                image.addEventListener('load', () => {
                    scene.textures.addImage(textureId, image);
                });
                image.src = url;
            }
            else {
                url = `${url}?t=canvas`;
                if (imageType === 'svg') {
                    scene.load.svg(textureId, url, { scale: GameplayRenderer.SVG_DEFAULT_SCALE() });
                }
                else {
                    scene.load.image(textureId, url);
                }
            }
            scene.load.start();
        }
        return {
            isLoading: !scene.textures.exists(textureId),
            textureId,
        };
    }
    static imageLoadedHook(textureId, scene, callback) {
        const hook = (key) => {
            if (key === textureId) {
                scene.textures.off(phaser_1.default.Textures.Events.ADD, hook);
                callback(textureId);
            }
        };
        scene.textures.on(phaser_1.default.Textures.Events.ADD, hook);
    }
    setBackgroundImage(imageUrl) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield ((_b = (_a = this.mainScene) === null || _a === void 0 ? void 0 : _a.bg) === null || _b === void 0 ? void 0 : _b.setUrl(imageUrl));
        });
    }
    displayTextBubble(props) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // console.debug('displayTextBubble', JSON.parse(JSON.stringify(props)));
            return (_b = (_a = this.mainScene) === null || _a === void 0 ? void 0 : _a.textBubble) === null || _b === void 0 ? void 0 : _b.display(props);
        });
    }
    /**
     * Hide currently displayed text bubble
     */
    hideTextBubble() {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return (_b = (_a = this === null || this === void 0 ? void 0 : this.mainScene) === null || _a === void 0 ? void 0 : _a.textBubble) === null || _b === void 0 ? void 0 : _b.hide();
        });
    }
    /**
     * Update currently displayed text bubble
     */
    updateTextBubble(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // console.debug('updateTextBubble', JSON.parse(JSON.stringify(data)));
            return this.mainScene.textBubble.updateBubbleData(data);
        });
    }
    updateTextBubbleTextOnly(text) {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return (_a = this.mainScene.textBubble) === null || _a === void 0 ? void 0 : _a.updateBubbleTextOnly(text);
        });
    }
    updateChoiceButtonsTextOnly(texts) {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return (_a = this.mainScene.textBubble) === null || _a === void 0 ? void 0 : _a.updateChoiceButtonsTextOnly(texts);
        });
    }
    updateChoiceButtonsAnalytics(analyticStep) {
        var _a;
        (_a = this.mainScene.textBubble) === null || _a === void 0 ? void 0 : _a.updateChoiceButtonsAnalytics(analyticStep);
    }
    updateChoiceButtonsAnalyticsViewType(viewType) {
        var _a;
        (_a = this.mainScene.textBubble) === null || _a === void 0 ? void 0 : _a.updateChoiceButtonsAnalyticsViewType(viewType);
    }
    hideChoiceButtonsAnalytics() {
        var _a;
        (_a = this.mainScene.textBubble) === null || _a === void 0 ? void 0 : _a.hideChoiceButtonsAnalytics();
    }
    getTextBubbleBounds() {
        return this.mainScene.textBubble.getBoundsData();
    }
    displayDropoffAnalytics(analytcisData, analyticsViewType = Analytics_1.AnalyticsViewType.COUNTS) {
        var _a;
        (_a = this.mainScene.dropoffAnalyticsView) === null || _a === void 0 ? void 0 : _a.init(analytcisData, analyticsViewType);
    }
    hideDropoffAnalytics() {
        var _a;
        (_a = this.mainScene.dropoffAnalyticsView) === null || _a === void 0 ? void 0 : _a.uninit();
    }
    updateDropoffAnalyticsViewType(analyticsViewType) {
        var _a;
        (_a = this.mainScene.dropoffAnalyticsView) === null || _a === void 0 ? void 0 : _a.changeViewType(analyticsViewType);
    }
    updateAnalyticsViewType(analyticsViewType) {
        this.updateChoiceButtonsAnalyticsViewType(analyticsViewType);
        this.updateDropoffAnalyticsViewType(analyticsViewType);
    }
    setClickAreaCallback(clickCallback) {
        this.mainScene.clickArea.setClickCallback(clickCallback);
    }
    enableClickArea() {
        this.mainScene.clickArea.enableClickArea();
    }
    disableClickArea() {
        this.mainScene.clickArea.disableClickArea();
    }
    setClickAreaEnabled(isEnabled) {
        this.mainScene.clickArea.setEnabled(isEnabled);
    }
    updateAvatar(avatarConfig, side, immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            switch (side) {
                case consts_1.SIDE.RIGHT:
                    return yield this.mainScene.updateRightAvatar(avatarConfig, immediate);
                case consts_1.SIDE.LEFT:
                    return yield this.mainScene.updateLeftAvatar(avatarConfig, immediate);
            }
        });
    }
    /**
     * Sliding in avatar
     */
    showAvatar(side, immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // switch (side) {
            //     case SIDE.RIGHT:
            //         await this.mainScene!.showRightAvatar(immediate);
            //         break;
            //     case SIDE.LEFT:
            //         await this.mainScene!.showLeftAvatar(immediate);
            //         break;
            // }
            // MICHAL: I leave it as this to do not break other implementations of GR
            return Promise.resolve({ side: side, immediate: immediate });
        });
    }
    hideAvatar(side, immediate = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            switch (side) {
                case consts_1.SIDE.RIGHT:
                    yield this.mainScene.hideRightAvatar(immediate);
                    break;
                case consts_1.SIDE.LEFT:
                    yield this.mainScene.hideLeftAvatar(immediate);
                    break;
            }
        });
    }
    stopAvatarLoading() {
        this.mainScene.preventAvatarLoadingRaceCondition();
    }
    /**
     * TODO: character props type
     */
    preloadAvatarResources(avatarConfig) {
        // console.log('%cpreloadAvatarResources', 'color: purple', avatarConfig);
        GameplayAvatar_1.GameplayAvatar.preloadByAvatarConfig(avatarConfig, this.mainScene, this.avatarResolver);
    }
    get leftAvatarState() {
        return this.mainScene.leftAvatarState;
    }
    get rightAvatarState() {
        return this.mainScene.rightAvatarState;
    }
    get isLeftAvatarVisible() {
        return !!this.mainScene.isLeftAvatarVisible;
    }
    get isRightAvatarVisible() {
        return !!this.mainScene.isRightAvatarVisible;
    }
    set leftAvatarEnabled(value) {
        this.mainScene.leftAvatarEnabled = value;
    }
    set rightAvatarEnabled(value) {
        this.mainScene.rightAvatarEnabled = value;
    }
    animateStoryEndingRightAvatar() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.mainScene.animateStoryEndingRightAvatar();
        });
    }
    showCurtainOverBackground() {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return (_a = this.mainScene) === null || _a === void 0 ? void 0 : _a.showCurtainOverBackground();
        });
    }
    static getUrlByImageKey(key) {
        const _url = Object.entries(this.imageMap)
            .find(([_url, _key]) => key === _key);
        if (!_url) {
            return undefined;
        }
        return _url[0];
    }
    static isLetterBox() {
        return this.letterboxRenderingType === LetterboxRenderingType.Letterbox;
    }
    static letterboxRatio() {
        return this.letterboxViewportSettings.viewportSize.width / this.letterboxViewportSettings.viewportSize.height;
    }
}
exports.GameplayRenderer = GameplayRenderer;
GameplayRenderer.imageMapId = 1;
GameplayRenderer.imageMap = {};
GameplayRenderer.letterboxDefaultViewportSettings = {
    viewportSize: {
        width: 1080,
        height: 1920,
    },
    viewportBgColor: 0x000000,
};
GameplayRenderer.alwaysRealDevicePixelRatio = 1;
GameplayRenderer.canvasScale = 1;
// optimization
GameplayRenderer.aggressiveMemoryManagement = false;
GameplayRenderer.onAssetUnloadHandler = ((_url) => false);
GameplayRenderer.mouseEventsEnabled = false;
GameplayRenderer.letterboxRenderingType = LetterboxRenderingType.Standard;
GameplayRenderer.letterboxViewportSettings = GameplayRenderer.letterboxDefaultViewportSettings;
